import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // NCC: an accordion block set to "Open one row at a time" closes its other rows as this one opens
    if (!this.details.open) {
      const group = this.closest('[data-single-open]');
      if (!group) return;
      let closing = false;
      let shrinkAbove = 0;
      const clickedTop = this.details.getBoundingClientRect().top;
      for (const details of group.querySelectorAll('accordion-custom > details[open]')) {
        if (details !== this.details && details.closest('[data-single-open]') === group) {
          // how much the page above the clicked row is about to lose: the row's height minus its summary
          if (details.getBoundingClientRect().top < clickedTop) {
            const summary = details.querySelector('summary');
            shrinkAbove += details.getBoundingClientRect().height - (summary ? summary.getBoundingClientRect().height : 0);
          }
          // the row has just lost focus to the clicked one, and Horizon drops the transition on unfocused rows:
          // this marker keeps it (snippets/accordion-custom-component.liquid) so the row eases shut (Miles, 2026-09-16)
          details.setAttribute('data-ncc-closing', '');
          details.open = false;
          setTimeout(() => details.removeAttribute('data-ncc-closing'), 500);
          closing = true;
        }
      }
      // a long row shutting ABOVE the clicked one pulls the page up under the pointer (a 1200px Description sent
      // the clicked Ingredients row off the top of the screen - Miles, 2026-09-17): keep the clicked row where
      // it was by scrolling with the shrink, frame by frame, until the close has settled - but only when the
      // shift would be a problem; for short FAQ answers the hold read as the page creeping down with every
      // row opened (Miles, 2026-09-20), so those rows simply move up with the row above them
      if (closing && this.#shouldAnchor(shrinkAbove)) this.#anchorWhileClosing();
    }
  };

  /**
   * Whether the clicked row needs holding: only if the rows closing above it would take its summary under the
   * sticky header (or off the top) or move it more than about a third of the screen.
   * @param {number} shrinkAbove - the height the rows above the clicked one are about to lose
   */
  #shouldAnchor(shrinkAbove) {
    if (shrinkAbove <= 0) return false;
    const anchor = this.details.querySelector('summary') ?? this.details;
    const header = document.querySelector('#header-component');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    return anchor.getBoundingClientRect().top - shrinkAbove < headerBottom + 24 || shrinkAbove > window.innerHeight * 0.35;
  }

  /**
   * Holds the clicked summary at its viewport position while sibling rows animate shut, adjusting the scroll
   * container by the same amount the summary moved each frame (written instantly: the page container scrolls
   * smoothly by CSS, so a plain scrollTop write would itself be eased).
   */
  #anchorWhileClosing() {
    const anchor = this.details.querySelector('summary') ?? this.details;
    const startTop = anchor.getBoundingClientRect().top;
    const started = performance.now();
    const CLOSE_DURATION = 500;
    let settled = 0;
    const step = () => {
      const delta = anchor.getBoundingClientRect().top - startTop;
      if (Math.abs(delta) >= 0.5) {
        scrollTo({ top: getScrollTop() + delta, behavior: 'instant' });
        settled = 0;
      } else if (performance.now() - started > CLOSE_DURATION) {
        settled += 1;
      }
      if (settled < 3 && performance.now() - started < CLOSE_DURATION + 400) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
